/**
 * Los bloques de la ficha que escribe una persona (T036–T041 · FR-001).
 *
 * POR QUÉ ESTÁ SEPARADO DEL FORMULARIO
 * ────────────────────────────────────
 * Porque son dos cosas distintas que se parecen. `nueva.tsx` recoge lo que hace
 * falta para **aprovisionar** —nombre, calendario, catálogo a sembrar, WhatsApp—
 * y esto recoge lo que el negocio **es**: cómo llama a las cosas, cómo suena, qué
 * autoriza a afirmar y cuándo pasa a una persona.
 *
 * DOS REGLAS QUE EXPLICAN CASI TODOS LOS CAMPOS
 * ─────────────────────────────────────────────
 *  1. **Nada se rellena solo al enviar.** Que el formulario llegue pre-relleno y
 *     a la vista es correcto y deseable: el operador ve el valor escrito y lo
 *     cambia si quiere, y lo que acaba en la ficha es una decisión suya. Lo
 *     prohibido es que un hueco vacío se complete por su cuenta, porque entonces
 *     la decisión deja de tener dueño (FR-015).
 *  2. **Los términos neutros también se declaran.** El bot jamás aplica un
 *     término que no se le haya proyectado, así que «cliente» viene escrito en
 *     vez de en blanco.
 */
import { Input } from '../../components/ui/input'
import { Label } from '../../components/ui/label'
import { Textarea } from '../../components/ui/textarea'
import type { FichaAlta, MapaCapacidades } from '../../lib/altas'
import {
    bienvenidaGenerica,
    largoFrase,
    recortarFrase,
    textoBienvenidaAgendify,
    TOPE_FRASE,
} from '../../lib/frases-negocio'

const CLASE_SELECT =
    'flex h-9 w-full rounded-md border border-neutral-700 bg-neutral-900 px-3 py-1 text-sm text-neutral-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-400'

const CAPACIDADES: Array<{ k: keyof MapaCapacidades; et: string; nota?: string }> = [
    { k: 'reservar', et: 'Reservar', nota: 'exige catálogo y reglas de reserva' },
    { k: 'modificar', et: 'Cambiar cita', nota: 'exige lo de reservar + ventana de cancelación' },
    { k: 'cancelar', et: 'Cancelar', nota: 'exige ventana de cancelación' },
    { k: 'responder', et: 'Responder dudas', nota: 'exige dirección o alguna pregunta frecuente' },
    { k: 'recordar', et: 'Recordatorios', nota: 'aviso 24 h antes, si el cliente escribió hace menos de un día' },
    { k: 'escalar', et: 'Pasar a una persona', nota: 'lo exige el artículo 50 del AI Act' },
]

const DISPARADORES: Array<{ k: keyof FichaAlta['escalada']['disparadores']; et: string }> = [
    { k: 'queja', et: 'Una queja' },
    { k: 'precioFueraDeLoNormal', et: 'Un precio fuera de lo normal' },
    { k: 'urgencia', et: 'Una urgencia' },
    { k: 'datosDeSalud', et: 'Datos de salud' },
]

const IDIOMAS: Array<{ k: 'es' | 'en' | 'it'; et: string }> = [
    { k: 'es', et: 'Español' },
    { k: 'en', et: 'Inglés' },
    { k: 'it', et: 'Italiano' },
]

/**
 * Los siete pasos del alta.
 *
 * La forma viene de la puesta en marcha del panel de negocio: pasos plegables,
 * uno abierto cada vez, y cada uno diciendo **qué pasa si falta** en vez de
 * marcarse con un asterisco. Un asterisco no explica nada; «sin esto el
 * asistente no sabe si hay hueco» sí.
 */
export const PASOS = [
    {
        k: 'identidad', ic: '🏷️', t: 'Quién es y cómo aparece', req: true,
        // El color no es decoración: es lo que deja distinguir de un vistazo en
        // qué parte del negocio estás. Con siete bloques iguales, todos parecen
        // el mismo formulario largo.
        color: 'sky',
        sin: 'Sin esto el negocio no existe para nadie: no hay a quién escribir ni con qué nombre contestar.',
        ia: null,
    },
    {
        k: 'catalogo', ic: '✂️', t: 'Qué ofrece y quién lo hace', req: true, color: 'violet',
        sin: 'Sin catálogo el asistente puede hablar, pero no reservar: no sabe qué se pide ni cuánto ocupa.',
        ia: 'catalogo',
        ejemplo: 'Cinco profesionales: dos solo de tarde, uno de mañana y dos a jornada completa.',
    },
    {
        k: 'reglas', ic: '📋', t: 'Reglas de reserva y cancelación', req: true, color: 'amber',
        sin: 'Sin reglas no sabe con cuánta antelación se reserva ni hasta cuándo se cancela. Y no se las inventa.',
        ia: 'reglas',
        ejemplo: 'Reservas con al menos 4 horas de antelación y cancelación hasta 48 h antes.',
    },
    {
        k: 'voz', ic: '🗣️', t: 'Cómo llama a las cosas y cómo suena', req: true, color: 'emerald',
        sin: 'Sin esto hablaría en genérico: «cliente» y «cita» aunque el negocio diga «paciente» y «sesión».',
        ia: 'vocabulario,voz',
        ejemplo: 'Que trate de usted y sea más bien seco, sin emojis.',
    },
    {
        k: 'conocimiento', ic: '❓', t: 'Qué puede afirmar', req: false, color: 'cyan',
        sin: 'Sin nada que afirmar, cada pregunta acaba en una persona. El negocio creería tener algo que no funciona.',
        ia: 'conocimiento',
        ejemplo: 'Añade preguntas sobre parking, formas de pago y si hay que venir con algo preparado.',
    },
    {
        k: 'escalada', ic: '🙋', t: 'Qué puede hacer y cuándo pasa a una persona', req: true, color: 'rose',
        sin: 'La salida a una persona no se puede apagar. Lo demás decide qué sabe hacer de verdad.',
        ia: 'escalada,capacidades',
        ejemplo: 'Que escale siempre ante quejas y que no pueda cancelar citas.',
    },
    {
        k: 'legal', ic: '⚖️', t: 'Legal e indicaciones', req: true, color: 'slate',
        sin: 'Sin el enlace a la política de privacidad, el aviso legal no tiene a dónde apuntar.',
        ia: null,
    },
    {
        k: 'contacto', ic: '📍', t: 'Cómo llegar y con quién hablar', req: false, color: 'teal',
        sin: 'Son los datos que el panel enseña a su equipo. El asistente no los usa para reservar.',
        ia: null,
    },
    {
        k: 'whatsapp', ic: '💬', t: 'El WhatsApp por el que le escriben', req: false, color: 'lime',
        sin: 'Sin número, el negocio queda «esperando WhatsApp»: existe y funciona, pero todavía no le llega ningún mensaje.',
        ia: null,
    },
] as const

/**
 * Las clases de color por bloque, escritas enteras.
 *
 * Tailwind borra lo que no encuentra escrito literalmente, así que un
 * `border-${color}-900` construido al vuelo no existiría en la hoja de estilos y
 * el bloque saldría sin color y sin dar error.
 */
export const COLORES: Record<string, { borde: string; fondo: string; texto: string }> = {
    sky: { borde: 'border-sky-900/60', fondo: 'bg-sky-950/20', texto: 'text-sky-300' },
    violet: { borde: 'border-violet-900/60', fondo: 'bg-violet-950/20', texto: 'text-violet-300' },
    amber: { borde: 'border-amber-900/60', fondo: 'bg-amber-950/20', texto: 'text-amber-300' },
    emerald: { borde: 'border-emerald-900/60', fondo: 'bg-emerald-950/20', texto: 'text-emerald-300' },
    cyan: { borde: 'border-cyan-900/60', fondo: 'bg-cyan-950/20', texto: 'text-cyan-300' },
    rose: { borde: 'border-rose-900/60', fondo: 'bg-rose-950/20', texto: 'text-rose-300' },
    slate: { borde: 'border-slate-700', fondo: 'bg-slate-900/40', texto: 'text-slate-300' },
    teal: { borde: 'border-teal-900/60', fondo: 'bg-teal-950/20', texto: 'text-teal-300' },
    lime: { borde: 'border-lime-900/60', fondo: 'bg-lime-950/20', texto: 'text-lime-300' },
}

export type ClavePaso = (typeof PASOS)[number]['k']

function Seccion({
    titulo,
    ayuda,
    children,
}: {
    titulo: string
    ayuda?: string
    children: React.ReactNode
}) {
    return (
        <section className="space-y-3 border-b border-neutral-800 pb-6">
            <div>
                <h2 className="text-xs font-semibold uppercase tracking-wide text-neutral-400">{titulo}</h2>
                {ayuda ? <p className="mt-1 text-xs text-neutral-500">{ayuda}</p> : null}
            </div>
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">{children}</div>
        </section>
    )
}

function Campo({ id, label, ayuda, children }: { id: string; label: string; ayuda?: string; children: React.ReactNode }) {
    return (
        <div className="space-y-1.5">
            <Label htmlFor={id}>{label}</Label>
            {children}
            {ayuda ? <p className="text-xs text-neutral-500">{ayuda}</p> : null}
        </div>
    )
}

function Casilla({
    id,
    label,
    nota,
    checked,
    disabled,
    onChange,
}: {
    id: string
    label: string
    nota?: string | undefined
    checked: boolean
    disabled?: boolean | undefined
    onChange: (v: boolean) => void
}) {
    /**
     * Una casilla bloqueada se lee como APAGADA, aunque esté marcada.
     *
     * El gris de `disabled` es el mismo que usa el navegador para «esto no
     * aplica», así que «pasar a una persona» —que va siempre encendida por
     * ley— parecía desactivada. Se dibuja como un candado con su marca, y con
     * el motivo escrito al lado: no es que no se pueda tocar, es que no se
     * negocia.
     */
    if (disabled) {
        return (
            <div className="flex items-start gap-2 rounded-md border border-emerald-900/50 bg-emerald-950/20 px-2 py-1.5 text-sm">
                <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded bg-emerald-500 text-[10px] font-bold text-neutral-950">
                    ✓
                </span>
                <span>
                    <span className="text-neutral-100">{label}</span>
                    <span className="ml-1.5 text-xs text-emerald-300">· siempre 🔒</span>
                    {nota ? <span className="block text-xs text-neutral-500">{nota}</span> : null}
                </span>
            </div>
        )
    }
    return (
        <label htmlFor={id} className="flex items-start gap-2 text-sm text-neutral-200">
            <input
                id={id}
                type="checkbox"
                className="mt-0.5 h-4 w-4 rounded border-neutral-700 bg-neutral-900"
                checked={checked}
                onChange={(e) => onChange(e.target.checked)}
            />
            <span>
                {label}
                {nota ? <span className="block text-xs text-neutral-500">{nota}</span> : null}
            </span>
        </label>
    )
}

function hoyIso(): string {
    const d = new Date()
    const m = String(d.getMonth() + 1).padStart(2, '0')
    const day = String(d.getDate()).padStart(2, '0')
    return `${d.getFullYear()}-${m}-${day}`
}

function FrasesAlEntrar({
    ficha,
    set,
}: {
    ficha: FichaAlta
    set: <K extends keyof FichaAlta>(k: K, v: FichaAlta[K]) => void
}) {
    const poner = (clave: 'bienvenida' | 'siempre', valor: string) => {
        const recortada = recortarFrase(valor)
        const { [clave]: _fuera, ...resto } = ficha.conocimiento
        set('conocimiento', recortada.trim() === '' && valor.trim() === ''
            ? resto
            : { ...ficha.conocimiento, [clave]: recortada })
    }
    const bienvenida = ficha.conocimiento.bienvenida ?? ''
    const siempre = ficha.conocimiento.siempre ?? ''
    const negocio = ficha.nombreVisible || 'este negocio'
    const generica = bienvenidaGenerica({
        nombre: negocio,
        servicios: ficha.catalogo.servicios.map((s) => s.nombre),
        trato: ficha.voz.tratamiento === 'tu' ? 'tu' : 'usted',
    })
    const bienvenidaEfectiva = bienvenida.trim() ? bienvenida : generica
    return (
        <div className="md:col-span-2 xl:col-span-3 grid gap-4 lg:grid-cols-2">
            <div className="space-y-3">
                <p className="text-xs text-neutral-500">
                    La de Agendify y la cláusula no se editan aquí: se dicen antes de aceptar. El cuadro es la frase
                    de este negocio, y se dice cuando la persona acepta. Si no escribes la tuya, se dice la genérica
                    que ves. Si la borras, vuelve esa. Un emoticono cuenta como un carácter. Tope {TOPE_FRASE}.
                </p>
                <Campo
                    id="fraseBienvenida"
                    label="Al entrar por primera vez"
                    ayuda={`${largoFrase(bienvenidaEfectiva)}/${TOPE_FRASE}`}
                >
                    <Textarea
                        id="fraseBienvenida"
                        rows={3}
                        value={bienvenidaEfectiva}
                        onChange={(e) => poner('bienvenida', e.target.value)}
                    />
                </Campo>
                <Campo
                    id="fraseSiempre"
                    label="También cuando ya es cliente"
                    ayuda={`${largoFrase(siempre)}/${TOPE_FRASE}`}
                >
                    <Textarea
                        id="fraseSiempre"
                        rows={3}
                        value={siempre}
                        onChange={(e) => poner('siempre', e.target.value)}
                    />
                </Campo>
            </div>
            <div className="space-y-3 rounded-lg border border-neutral-800 bg-neutral-950 p-3 text-sm text-neutral-200">
                <p className="text-[11px] uppercase tracking-wide text-neutral-500">Antes de aceptar · no se edita</p>
                <p className="whitespace-pre-wrap">{textoBienvenidaAgendify(negocio)}</p>
                <p className="text-xs text-neutral-500">Después, la cláusula, con Aceptar y Rechazar.</p>
                <p className="text-[11px] uppercase tracking-wide text-neutral-500">Después de aceptar</p>
                <p className="whitespace-pre-wrap">{bienvenidaEfectiva}</p>
                {siempre.trim() && <p className="whitespace-pre-wrap">{siempre}</p>}
                <p className="text-[11px] uppercase tracking-wide text-neutral-500">Vista previa · quien ya es cliente</p>
                {siempre.trim() ? (
                    <p className="whitespace-pre-wrap">{siempre}</p>
                ) : (
                    <p className="text-xs text-neutral-500">Sin frase, no se añade nada de este negocio.</p>
                )}
            </div>
        </div>
    )
}

export function CamposDeLaFicha({
    ficha,
    onChange,
    /** Si se pasa, solo se pintan las secciones de ese paso. */
    paso,
}: {
    ficha: FichaAlta
    onChange: (f: FichaAlta) => void
    paso?: ClavePaso
}) {
    const ver = (k: ClavePaso) => paso === undefined || paso === k
    const set = <K extends keyof FichaAlta>(k: K, v: FichaAlta[K]) => onChange({ ...ficha, [k]: v })
    const asignacion = ficha.reglas.asignacion ?? { criterio: 'masLiberado' as const }

    return (
        <>
            {ver('identidad') && (
            <Seccion
                titulo="Cómo aparece y en qué punto está"
                ayuda="«Demo» y «en prueba» ya se atienden. «Pausado» deja de contestar al cliente sin dar explicación, así que no es lo mismo que dar de baja."
            >
                <Campo id="estado" label="Estado">
                    <select
                        id="estado"
                        className={CLASE_SELECT}
                        value={ficha.estado}
                        onChange={(e) => set('estado', e.target.value as FichaAlta['estado'])}
                    >
                        <option value="demo">Demo</option>
                        <option value="en_prueba">En prueba</option>
                        <option value="activo">Activo</option>
                        <option value="pausado">Pausado — deja de contestar</option>
                    </select>
                </Campo>
                <Campo
                    id="nombreVisible"
                    label="Nombre en el canal"
                    ayuda="Con el que el cliente lo ve escribiendo. No tiene por qué ser la razón social."
                >
                    <Input
                        id="nombreVisible"
                        value={ficha.nombreVisible}
                        onChange={(e) => set('nombreVisible', e.target.value)}
                    />
                </Campo>
            </Seccion>
            )}

            {ver('voz') && (
            <Seccion
                titulo="Cómo llama a las cosas"
                ayuda="Van siempre, aunque sean las palabras de siempre: el asistente solo usa las que se le den escritas."
            >
                {(['cliente', 'cita', 'profesional', 'servicio'] as const).map((k) => (
                    <Campo key={k} id={`voc-${k}`} label={k[0]!.toUpperCase() + k.slice(1)}>
                        <Input
                            id={`voc-${k}`}
                            value={ficha.vocabulario[k]}
                            onChange={(e) => set('vocabulario', { ...ficha.vocabulario, [k]: e.target.value })}
                        />
                    </Campo>
                ))}
            </Seccion>
            )}

            {ver('voz') && (
            <Seccion titulo="Cómo suena" ayuda="Lo anterior es cómo se llaman las cosas; esto es cómo suena quien las dice.">
                <Campo id="tratamiento" label="Trato">
                    <select
                        id="tratamiento"
                        className={CLASE_SELECT}
                        value={ficha.voz.tratamiento}
                        onChange={(e) => set('voz', { ...ficha.voz, tratamiento: e.target.value as FichaAlta['voz']['tratamiento'] })}
                    >
                        <option value="tu">De tú</option>
                        <option value="usted">De usted</option>
                        <option value="espejo">Como le hablen</option>
                    </select>
                </Campo>
                <Campo id="extension" label="Cuánto se extiende">
                    <select
                        id="extension"
                        className={CLASE_SELECT}
                        value={ficha.voz.extension}
                        onChange={(e) => set('voz', { ...ficha.voz, extension: e.target.value as FichaAlta['voz']['extension'] })}
                    >
                        <option value="breve">Breve</option>
                        <option value="normal">Normal</option>
                        <option value="amplia">Amplia</option>
                    </select>
                </Campo>
                <Campo id="emojis" label="Emojis">
                    <select
                        id="emojis"
                        className={CLASE_SELECT}
                        value={ficha.voz.emojis}
                        onChange={(e) => set('voz', { ...ficha.voz, emojis: e.target.value as FichaAlta['voz']['emojis'] })}
                    >
                        <option value="ninguno">Ninguno</option>
                        <option value="alguno">Alguno</option>
                        <option value="soltura">Con soltura</option>
                    </select>
                </Campo>
                <Campo id="presentacion" label="Cómo se presenta">
                    <Input
                        id="presentacion"
                        value={ficha.voz.presentacion}
                        onChange={(e) => set('voz', { ...ficha.voz, presentacion: e.target.value })}
                    />
                </Campo>
                <div className="md:col-span-2 xl:col-span-3">
                    <Label>Idiomas en los que puede contestar</Label>
                    <div className="mt-1.5 flex flex-wrap gap-4">
                        {IDIOMAS.map((i) => (
                            <Casilla
                                key={i.k}
                                id={`idioma-${i.k}`}
                                label={i.et}
                                checked={ficha.voz.idiomas.includes(i.k)}
                                onChange={(on) =>
                                    set('voz', {
                                        ...ficha.voz,
                                        idiomas: on
                                            ? [...ficha.voz.idiomas, i.k]
                                            : ficha.voz.idiomas.filter((x) => x !== i.k),
                                    })
                                }
                            />
                        ))}
                    </div>
                    <p className="mt-1 text-xs text-neutral-500">
                        {ficha.voz.idiomas[0] ? (
                            <>
                                Por defecto: {IDIOMAS.find((x) => x.k === ficha.voz.idiomas[0])?.et}. El
                                primero de la lista es con el que se empieza cuando el primer mensaje no da
                                para detectarlo. Reordenar es cambiar de idioma.
                            </>
                        ) : (
                            'Marca al menos uno. El primero es el idioma por defecto.'
                        )}
                    </p>
                    <p className="mt-1 text-xs text-neutral-500">
                        La lista está cerrada a propósito: un idioma entra el día que tiene su texto legal
                        escrito, porque el asistente tiene prohibido traducirlo por su cuenta.
                    </p>
                </div>
                <div className="md:col-span-2 xl:col-span-3">
                    <Campo
                        id="nuncaDecir"
                        label="Lo que no debe decir nunca"
                        ayuda="No es estilo, es un límite. Aquí se escribe «no prometer resultados» o «no dar consejo médico». Puede quedarse vacío, pero hay que haberse hecho la pregunta."
                    >
                        <Textarea
                            id="nuncaDecir"
                            rows={2}
                            value={ficha.voz.nuncaDecir}
                            onChange={(e) => set('voz', { ...ficha.voz, nuncaDecir: e.target.value })}
                        />
                    </Campo>
                </div>
            </Seccion>
            )}

            {ver('escalada') && (
            <Seccion
                titulo="Qué puede hacer"
                ayuda="La izquierda es lo que el plan incluye; la derecha, lo que el negocio quiere encendido. Solo funciona lo que esté en las dos."
            >
                <div className="grid gap-4 md:col-span-2 md:grid-cols-2 xl:col-span-3">
                    <div className="space-y-2">
                        <Label>Incluido en el plan</Label>
                        {CAPACIDADES.map((c) => (
                            <Casilla
                                key={c.k}
                                id={`con-${c.k}`}
                                label={c.et}
                                nota={c.nota}
                                checked={ficha.capacidades.concedidas[c.k]}
                                disabled={c.k === 'escalar'}
                                onChange={(v) =>
                                    set('capacidades', {
                                        ...ficha.capacidades,
                                        concedidas: { ...ficha.capacidades.concedidas, [c.k]: v },
                                    })
                                }
                            />
                        ))}
                    </div>
                    <div className="space-y-2">
                        <Label>Encendido por el negocio</Label>
                        {CAPACIDADES.map((c) => (
                            <Casilla
                                key={c.k}
                                id={`act-${c.k}`}
                                label={c.et}
                                checked={ficha.capacidades.activas[c.k]}
                                // No es un ajuste: poder pedir una persona lo exige
                                // el artículo 50 y no puede configurarse a apagado.
                                disabled={c.k === 'escalar'}
                                onChange={(v) =>
                                    set('capacidades', {
                                        ...ficha.capacidades,
                                        activas: { ...ficha.capacidades.activas, [c.k]: v },
                                    })
                                }
                            />
                        ))}
                    </div>
                </div>
            </Seccion>
            )}

            {ver('escalada') && (
            <Seccion
                titulo="Cuándo entra una persona"
                ayuda="Además de cuando el asistente dude. Pasar a una persona no se puede desactivar."
            >
                <div className="md:col-span-2 xl:col-span-3 space-y-2">
                    {DISPARADORES.map((d) => (
                        <Casilla
                            key={d.k}
                            id={`disp-${d.k}`}
                            label={d.et}
                            checked={ficha.escalada.disparadores[d.k]}
                            onChange={(v) =>
                                set('escalada', {
                                    ...ficha.escalada,
                                    disparadores: { ...ficha.escalada.disparadores, [d.k]: v },
                                })
                            }
                        />
                    ))}
                </div>
                <Campo
                    id="aclaraciones"
                    label="Cuántas veces repregunta antes de pasar"
                    ayuda="Entre 2 y 5. Menos y se rinde ante cualquier errata; más y la conversación se vuelve una tortura para quien no eligió el ajuste."
                >
                    <Input
                        id="aclaraciones"
                        type="number"
                        min={2}
                        max={5}
                        value={ficha.escalada.aclaracionesAntesDeEscalar}
                        onChange={(e) =>
                            set('escalada', {
                                ...ficha.escalada,
                                aclaracionesAntesDeEscalar: Number(e.target.value),
                            })
                        }
                    />
                </Campo>
            </Seccion>
            )}

            {ver('conocimiento') && (
            <Seccion
                titulo="Qué puede afirmar"
                ayuda="Fuera de aquí el asistente no improvisa: pasa a una persona. Con «responder dudas» encendido hace falta al menos la dirección o una pregunta."
            >
                <div className="md:col-span-2 xl:col-span-3">
                    <Campo id="direccion" label="Dirección">
                        <Input
                            id="direccion"
                            value={ficha.conocimiento.ubicacion?.direccion ?? ''}
                            onChange={(e) =>
                                {
                                    const { ubicacion: _fuera, ...resto } = ficha.conocimiento
                                    set(
                                        'conocimiento',
                                        e.target.value
                                            ? { ...resto, ubicacion: { direccion: e.target.value } }
                                            : resto,
                                    )
                                }
                            }
                        />
                    </Campo>
                </div>
                <Campo
                    id="telefonoNegocio"
                    label="Teléfono del negocio"
                    ayuda="Opcional. El asistente lo recita al final cuando no sabe algo. No se valida como número: extensiones y prefijos valen."
                >
                    <Input
                        id="telefonoNegocio"
                        value={ficha.conocimiento.telefono ?? ''}
                        onChange={(e) => {
                            const tel = e.target.value
                            const { telefono: _t, ...resto } = ficha.conocimiento
                            set('conocimiento', tel.trim() === '' ? resto : { ...ficha.conocimiento, telefono: tel })
                        }}
                    />
                    </Campo>
                <Campo
                    id="telefonoDueno"
                    label="WhatsApp del dueño"
                    ayuda="Opcional. Nueve dígitos, 6 o 7 (móvil) o 5 (demo). No es el teléfono de la clínica: es quién manda por WhatsApp. Vacío = el asistente no tiene dueño."
                >
                    <Input
                        id="telefonoDueno"
                        inputMode="tel"
                        value={ficha.dueno?.telefono ?? ''}
                        onChange={(e) => {
                            const tel = e.target.value.replace(/\D/g, '').slice(-9)
                            if (tel === '') {
                                const { dueno: _d, ...resto } = ficha
                                onChange(resto)
                                return
                            }
                            set('dueno', { telefono: tel })
                        }}
                    />
                </Campo>
                <div className="md:col-span-2 xl:col-span-3 space-y-2">
                    <Label>Preguntas frecuentes</Label>
                    {ficha.conocimiento.faq.map((f, i) => (
                        <div key={i} className="grid gap-2 md:grid-cols-2">
                            <Input
                                placeholder="Pregunta"
                                value={f.pregunta}
                                onChange={(e) => {
                                    const faq = [...ficha.conocimiento.faq]
                                    faq[i] = { ...f, pregunta: e.target.value }
                                    set('conocimiento', { ...ficha.conocimiento, faq })
                                }}
                            />
                            <Input
                                placeholder="Respuesta"
                                value={f.respuesta}
                                onChange={(e) => {
                                    const faq = [...ficha.conocimiento.faq]
                                    faq[i] = { ...f, respuesta: e.target.value }
                                    set('conocimiento', { ...ficha.conocimiento, faq })
                                }}
                            />
                        </div>
                    ))}
                    <button
                        type="button"
                        className="text-xs text-neutral-400 underline"
                        onClick={() =>
                            set('conocimiento', {
                                ...ficha.conocimiento,
                                faq: [...ficha.conocimiento.faq, { pregunta: '', respuesta: '' }],
                            })
                        }
                    >
                        Añadir pregunta
                    </button>
                </div>
                <FrasesAlEntrar ficha={ficha} set={set} />
            </Seccion>
            )}

            {ver('reglas') && (
            <Seccion titulo="Reglas de reserva">
                <Campo
                    id="antelacionMin"
                    label="Antelación mínima (minutos)"
                    ayuda="También se escribe en la agenda: los dos números tienen que coincidir."
                >
                    <Input
                        id="antelacionMin"
                        type="number"
                        min={0}
                        value={ficha.reglas.antelacionMinimaReservaMin}
                        onChange={(e) =>
                            set('reglas', { ...ficha.reglas, antelacionMinimaReservaMin: Number(e.target.value) })
                        }
                    />
                </Campo>
                <Campo
                    id="horizonte"
                    label="Hasta cuántos días se abre la agenda"
                    ayuda="El cuadrante de los profesionales tiene que llegar al menos hasta aquí, o se ofrecerán horas de un periodo que nadie ha definido."
                >
                    <Input
                        id="horizonte"
                        type="number"
                        min={1}
                        value={ficha.reglas.horizonteMaximoDias}
                        onChange={(e) => set('reglas', { ...ficha.reglas, horizonteMaximoDias: Number(e.target.value) })}
                    />
                </Campo>
                <Campo id="maxActivas" label="Citas activas por cliente">
                    <Input
                        id="maxActivas"
                        type="number"
                        min={1}
                        value={ficha.reglas.maxCitasActivasPorCliente}
                        onChange={(e) =>
                            set('reglas', { ...ficha.reglas, maxCitasActivasPorCliente: Number(e.target.value) })
                        }
                    />
                </Campo>
                <Campo id="ventanaCancel" label="Horas mínimas para tocar una cita">
                    <Input
                        id="ventanaCancel"
                        type="number"
                        min={0}
                        value={ficha.reglas.antelacionCancelacionHoras}
                        onChange={(e) =>
                            set('reglas', { ...ficha.reglas, antelacionCancelacionHoras: Number(e.target.value) })
                        }
                    />
                </Campo>
                <div className="md:col-span-2 xl:col-span-3">
                    <Casilla
                        id="mismoDia"
                        label="Permite reservar el mismo día"
                        checked={ficha.reglas.permiteMismoDia}
                        onChange={(v) => set('reglas', { ...ficha.reglas, permiteMismoDia: v })}
                    />
                </div>
                <Campo id="asignacion" label="Si al cliente le da igual, la cita se asigna">
                    <select
                        id="asignacion"
                        className={CLASE_SELECT}
                        value={asignacion.criterio}
                        onChange={(e) => {
                            const criterio = e.target.value as FichaAlta['reglas']['asignacion']['criterio']
                            set('reglas', {
                                ...ficha.reglas,
                                asignacion:
                                    criterio === 'fijo'
                                        ? { criterio, profesionalIndice: asignacion.profesionalIndice ?? 0 }
                                        : { criterio },
                            })
                        }}
                    >
                        <option value="masLiberado">Al que tenga la agenda más libre</option>
                        <option value="aleatorio">Al azar entre quien pueda</option>
                        <option value="fijo">Siempre al mismo profesional</option>
                    </select>
                </Campo>
                {asignacion.criterio === 'fijo' && (
                    <Campo id="profesionalFijo" label="Qué profesional">
                        <select
                            id="profesionalFijo"
                            className={CLASE_SELECT}
                            value={asignacion.profesionalIndice ?? 0}
                            onChange={(e) =>
                                set('reglas', {
                                    ...ficha.reglas,
                                    asignacion: {
                                        criterio: 'fijo',
                                        profesionalIndice: Number(e.target.value),
                                    },
                                })
                            }
                        >
                            {ficha.catalogo.profesionales.map((p, i) => (
                                <option key={i} value={i}>
                                    {p.nombre || `Profesional ${i + 1}`}
                                </option>
                            ))}
                        </select>
                    </Campo>
                )}
            </Seccion>
            )}

            {ver('catalogo') && (
            <Seccion
                titulo="Catálogo"
                ayuda="La duración es la que se le anuncia al cliente. El margen son los minutos entre una cita y la siguiente: en la agenda se bloquea la suma de los dos."
            >
                <div className="md:col-span-2 xl:col-span-3 space-y-3">
                    {ficha.catalogo.servicios.map((s, i) => (
                        <div key={i} className="grid gap-2 md:grid-cols-2 xl:grid-cols-4">
                            <Input
                                placeholder="Servicio"
                                value={s.nombre}
                                onChange={(e) => {
                                    const servicios = [...ficha.catalogo.servicios]
                                    servicios[i] = { ...s, nombre: e.target.value }
                                    set('catalogo', { ...ficha.catalogo, servicios })
                                }}
                            />
                            <Input
                                type="number"
                                min={1}
                                placeholder="Duración"
                                value={s.duracionMin}
                                onChange={(e) => {
                                    const servicios = [...ficha.catalogo.servicios]
                                    servicios[i] = { ...s, duracionMin: Number(e.target.value) }
                                    set('catalogo', { ...ficha.catalogo, servicios })
                                }}
                            />
                            <Input
                                type="number"
                                min={0}
                                placeholder="Margen"
                                value={s.margenMin}
                                onChange={(e) => {
                                    const servicios = [...ficha.catalogo.servicios]
                                    servicios[i] = { ...s, margenMin: Number(e.target.value) }
                                    set('catalogo', { ...ficha.catalogo, servicios })
                                }}
                            />
                            <Input
                                type="number"
                                min={0}
                                placeholder="Precio"
                                value={s.precio ?? ''}
                                onChange={(e) => {
                                    const servicios = [...ficha.catalogo.servicios]
                                    const precio = e.target.value === '' ? undefined : Number(e.target.value)
                                    servicios[i] = { ...s, ...(precio === undefined ? {} : { precio }) }
                                    set('catalogo', { ...ficha.catalogo, servicios })
                                }}
                            />
                        </div>
                    ))}
                    <button
                        type="button"
                        className="text-xs text-neutral-400 underline"
                        onClick={() =>
                            set('catalogo', {
                                ...ficha.catalogo,
                                servicios: [
                                    ...ficha.catalogo.servicios,
                                    { nombre: '', duracionMin: 45, margenMin: 5 },
                                ],
                            })
                        }
                    >
                        Añadir servicio
                    </button>
                </div>

                <div className="md:col-span-2 xl:col-span-3 space-y-3">
                    <Label>Quién presta qué</Label>
                    {ficha.catalogo.profesionales.map((p, i) => (
                        <div key={i} className="space-y-1.5">
                            <Input
                                placeholder="Profesional"
                                value={p.nombre}
                                onChange={(e) => {
                                    const profesionales = [...ficha.catalogo.profesionales]
                                    profesionales[i] = { ...p, nombre: e.target.value }
                                    set('catalogo', { ...ficha.catalogo, profesionales })
                                }}
                            />
                            <div className="flex flex-wrap gap-3">
                                {ficha.catalogo.servicios.map((s, si) => (
                                    <Casilla
                                        key={si}
                                        id={`pres-${i}-${si}`}
                                        label={s.nombre || `Servicio ${si + 1}`}
                                        checked={p.serviciosIndices.includes(si)}
                                        onChange={(on) => {
                                            const profesionales = [...ficha.catalogo.profesionales]
                                            profesionales[i] = {
                                                ...p,
                                                serviciosIndices: on
                                                    ? [...p.serviciosIndices, si].sort()
                                                    : p.serviciosIndices.filter((x) => x !== si),
                                            }
                                            set('catalogo', { ...ficha.catalogo, profesionales })
                                        }}
                                    />
                                ))}
                            </div>
                        </div>
                    ))}
                    <button
                        type="button"
                        className="text-xs text-neutral-400 underline"
                        onClick={() =>
                            set('catalogo', {
                                ...ficha.catalogo,
                                profesionales: [
                                    ...ficha.catalogo.profesionales,
                                    { nombre: '', serviciosIndices: [] },
                                ],
                            })
                        }
                    >
                        Añadir profesional
                    </button>
                    <p className="text-xs text-neutral-500">
                        Un servicio que no presta nadie no da error: simplemente no se le ofrecerá nunca a
                        ningún cliente.
                    </p>
                </div>
            </Seccion>
            )}

            {ver('legal') && (
            <Seccion titulo="Legal">
                <div className="md:col-span-2 xl:col-span-3">
                    <Campo
                        id="privacidad"
                        label="Política de privacidad"
                        ayuda="Viene puesta la de la plataforma. Si el negocio tiene la suya, se cambia aquí."
                    >
                        <Input
                            id="privacidad"
                            type="url"
                            value={ficha.legal.privacyPolicyUrl}
                            onChange={(e) =>
                                set('legal', {
                                    ...ficha.legal,
                                    privacyPolicyUrl: e.target.value,
                                    privacyPolicyDate: hoyIso(),
                                })
                            }
                        />
                    </Campo>
                </div>
                <Campo
                    id="fechaPolitica"
                    label="Desde cuándo rige esa política"
                    ayuda="AAAA-MM-DD. Sin ella no hay con qué comparar un consentimiento. Si cambias el enlace, se actualiza a hoy — se puede editar."
                >
                    <Input
                        id="fechaPolitica"
                        type="date"
                        value={ficha.legal.privacyPolicyDate ?? ''}
                        onChange={(e) =>
                            set('legal', { ...ficha.legal, privacyPolicyDate: e.target.value })
                        }
                    />
                </Campo>
                <div className="md:col-span-2 xl:col-span-3">
                    <Campo
                        id="cancelTarde"
                        label="Qué decir cuando alguien cancela tarde"
                        ayuda="Fuera de la ventana de cancelación."
                    >
                        <Textarea
                            id="cancelTarde"
                            rows={2}
                            value={ficha.legal.lateCancelPolicyMsg ?? ''}
                            onChange={(e) =>
                                set('legal', { ...ficha.legal, lateCancelPolicyMsg: e.target.value })
                            }
                        />
                    </Campo>
                </div>
                <div className="md:col-span-2 xl:col-span-3 space-y-2">
                    <Casilla
                        id="publicarPrecios"
                        label="Publicar precios"
                        nota="Autoriza a decir los precios que ya están en el catálogo. Un negocio nuevo llega en no."
                        checked={ficha.publicarPrecios === true}
                        onChange={(v) => set('publicarPrecios', v)}
                    />
                    <Casilla
                        id="leerNotasPrivadas"
                        label="Leer las notas privadas del equipo"
                        nota="Si se le devuelve una conversación, el asistente ve lo que el equipo escribió dentro. Los de demo van en no."
                        checked={ficha.leerNotasPrivadas === true}
                        onChange={(v) => set('leerNotasPrivadas', v)}
                    />
                </div>
                <p className="md:col-span-2 xl:col-span-3 text-xs text-neutral-500">
                    Los textos de consentimiento ya no se escriben aquí: los pone el asistente con una
                    plantilla propia, y el modelo tiene prohibido redactarlos. Que un cliente lo aceptó
                    queda apuntado en su ficha de contacto.
                </p>
            </Seccion>
            )}

            {ver('legal') && (
            <Seccion
                titulo="Indicaciones para el asistente"
                ayuda="Opcional. Cosas concretas que este negocio quiere que tenga en cuenta."
            >
                <div className="md:col-span-2 xl:col-span-3">
                    <Campo
                        id="instruccionesExtra"
                        label="Qué debe tener en cuenta"
                        ayuda="Se revisa antes de aceptarse. Y se le da al asistente como preferencia del negocio, nunca como orden: no puede saltarse las reglas de la plataforma ni apagar la salida a una persona."
                    >
                        <Textarea
                            id="instruccionesExtra"
                            rows={3}
                            value={ficha.instruccionesExtra ?? ''}
                            onChange={(e) => set('instruccionesExtra', e.target.value)}
                        />
                    </Campo>
                </div>
            </Seccion>
            )}

            {ver('contacto') && (
            <Seccion
                titulo="Quién administra el negocio"
                ayuda="Cuántas personas van a entrar al panel de este negocio. No es el equipo de la agenda."
            >
                <Campo
                    id="nAdministradores"
                    label="Nº de administradores"
                    ayuda="Opcional. Viaja en la plantilla. No se manda al asistente."
                >
                    <Input
                        id="nAdministradores"
                        type="number"
                        min={1}
                        step={1}
                        inputMode="numeric"
                        value={ficha.nAdministradores ?? ''}
                        onChange={(e) => {
                            const crudo = e.target.value.trim()
                            if (crudo === '') {
                                const { nAdministradores: _omit, ...resto } = ficha
                                onChange(resto)
                                return
                            }
                            const n = Number(crudo)
                            if (Number.isInteger(n) && n >= 1) set('nAdministradores', n)
                        }}
                    />
                </Campo>
            </Seccion>
            )}
        </>
    )
}
